import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { Cake as CakeIcon } from '@mui/icons-material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { CalendarDay, TimeOffRequest, PublicHoliday, TimeOffType, Event, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';

// Nautilus Console Design System Colors
const NAUTILUS = {
  deepOcean: '#0f2134',
  sand: '#ccba9b',
  abyssal: '#0a1622',
};

type CalendarView = 'month' | 'week';

interface PersonOption {
  userId: number;
  userName: string;
  userTrigram: string;
}

interface TimeOffCalendarProps {
  selectedDates?: string[];
  onDateSelect: (date: string) => void;
  timeOffRequests: TimeOffRequest[];
  publicHolidays: PublicHoliday[];
  events?: Event[];
  birthdays?: User[];
  disabledDates?: string[];
  disablePublicHolidays?: boolean;
  userOffice?: string;
  onApproveRequest?: (requestId: number) => void;
  onRejectRequest?: (requestId: number) => void;
  onMonthChange?: (month: Date) => void;
}

/** ISO week number (Mon-based), same formula as TimesheetPage */
const getWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getMonday = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const formatLocalDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const shortMonth = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'short' });

const TimeOffCalendar: React.FC<TimeOffCalendarProps> = ({
  selectedDates = [],
  onDateSelect,
  timeOffRequests,
  publicHolidays,
  events = [],
  birthdays = [],
  disabledDates = [],
  disablePublicHolidays = true,
  userOffice,
  onApproveRequest,
  onRejectRequest,
  onMonthChange,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [view, setView] = useState<CalendarView>('month');
  const [selectedPeople, setSelectedPeople] = useState<PersonOption[]>([]);
  const [showEvents, setShowEvents] = useState(true);
  const [showBirthdays, setShowBirthdays] = useState(true);

  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';

  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onMonthChange?.(currentMonth);
  }, [currentMonth, onMonthChange]);

  const handleMouseEnter = (date: string) => {
    if (view === 'week') return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setExpandedDate(date);
    }, 2000);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setExpandedDate(null);
  };

  const personOptions = useMemo((): PersonOption[] => {
    const map = new Map<number, PersonOption>();
    timeOffRequests.forEach((req) => {
      if (!map.has(req.userId)) {
        map.set(req.userId, {
          userId: req.userId,
          userName: req.userName,
          userTrigram: req.userTrigram,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.userName.localeCompare(b.userName)
    );
  }, [timeOffRequests]);

  const selectedUserIds = useMemo(
    () => new Set(selectedPeople.map((p) => p.userId)),
    [selectedPeople]
  );

  const holidaysByDate = useMemo(() => {
    const map = new Map<string, PublicHoliday[]>();
    for (const holiday of publicHolidays) {
      const list = map.get(holiday.date) || [];
      list.push(holiday);
      map.set(holiday.date, list);
    }
    return map;
  }, [publicHolidays]);

  const requestsByDate = useMemo(() => {
    const map = new Map<string, TimeOffRequest[]>();
    for (const req of timeOffRequests) {
      if (req.status !== 'approved' && req.status !== 'pending') continue;
      const list = map.get(req.date) || [];
      list.push(req);
      map.set(req.date, list);
    }
    return map;
  }, [timeOffRequests]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const event of events) {
      const list = map.get(event.event_date) || [];
      list.push(event);
      map.set(event.event_date, list);
    }
    return map;
  }, [events]);

  const birthdaysByMonthDay = useMemo(() => {
    const map = new Map<string, User[]>();
    for (const birthdayUser of birthdays) {
      if (!birthdayUser.birthday) continue;
      const bdayDate = new Date(birthdayUser.birthday);
      const key = `${bdayDate.getMonth() + 1}-${bdayDate.getDate()}`;
      const list = map.get(key) || [];
      list.push(birthdayUser);
      map.set(key, list);
    }
    return map;
  }, [birthdays]);

  const buildDay = (
    dateStr: string,
    year: number,
    monthNum: number,
    day: number,
    isCurrentMonth: boolean,
    todayStr: string
  ): CalendarDay => {
    const dateObj = new Date(year, monthNum - 1, day);
    const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
    const isToday = dateStr === todayStr;

    const dayHolidays = holidaysByDate.get(dateStr) || [];
    const isPublicHoliday = dayHolidays.length > 0;

    const hasBlockedHoliday = dayHolidays.some((holiday) => {
      if (!userOffice) return true;
      const holidayOffice = (holiday as { office?: string }).office;
      if (holidayOffice) {
        return holidayOffice === userOffice || holidayOffice === 'ALL';
      }
      const isUK = holiday.name.includes('(UK)');
      const isFR = holiday.name.includes('(FR)');
      if (isUK || isFR) {
        return (userOffice === 'UK' && isUK) || (userOffice === 'FR' && isFR);
      }
      return true;
    });

    const publicHolidayName =
      dayHolidays.length === 1
        ? dayHolidays[0].name
        : dayHolidays.length > 1
          ? `${dayHolidays.length} holidays`
          : undefined;

    let dayRequests = requestsByDate.get(dateStr) || [];
    if (selectedUserIds.size > 0) {
      dayRequests = dayRequests.filter((req) => selectedUserIds.has(req.userId));
    }

    const dayEvents = showEvents
      ? (eventsByDate.get(dateStr) || [])
      : [];

    let dayBirthdays = showBirthdays
      ? (birthdaysByMonthDay.get(`${monthNum}-${day}`) || [])
      : [];
    if (selectedUserIds.size > 0) {
      dayBirthdays = dayBirthdays.filter((u) => selectedUserIds.has(u.id));
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);
    const isPast = new Date(dateStr) < today;
    const isWithinWeek = new Date(dateStr) >= oneWeekAgo;

    const isDisabled =
      (!isCurrentMonth && !(isPast && isWithinWeek)) ||
      isWeekend ||
      disabledDates.includes(dateStr) ||
      (disablePublicHolidays && hasBlockedHoliday);

    return {
      date: dateStr,
      isWeekend,
      isPublicHoliday,
      publicHolidayName,
      timeOffRequests: dayRequests,
      events: dayEvents,
      birthdays: dayBirthdays,
      isToday,
      isDisabled,
    };
  };

  // Month calendar data
  const calendarData = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - ((firstDay.getDay() + 6) % 7));

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + ((7 - lastDay.getDay()) % 7));

    const days: CalendarDay[] = [];
    const current = new Date(startDate);
    const todayRef = new Date();
    const todayStr = formatLocalDate(todayRef);

    while (current <= endDate) {
      const y = current.getFullYear();
      const monthNum = current.getMonth() + 1;
      const day = current.getDate();
      const dateStr = formatLocalDate(current);
      const isCurrentMonth = current.getMonth() === month;

      days.push(buildDay(dateStr, y, monthNum, day, isCurrentMonth, todayStr));
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [
    currentMonth,
    holidaysByDate,
    requestsByDate,
    eventsByDate,
    birthdaysByMonthDay,
    disabledDates,
    userOffice,
    user,
    selectedUserIds,
    showEvents,
    showBirthdays,
    disablePublicHolidays,
  ]);

  // Week calendar data (Mon–Sun of currentWeek)
  const weekData = useMemo(() => {
    const monday = getMonday(currentMonth);
    const todayRef = new Date();
    const todayStr = formatLocalDate(todayRef);
    const days: CalendarDay[] = [];

    for (let i = 0; i < 7; i++) {
      const current = new Date(monday);
      current.setDate(monday.getDate() + i);
      const y = current.getFullYear();
      const monthNum = current.getMonth() + 1;
      const day = current.getDate();
      const dateStr = formatLocalDate(current);
      // In week view all days of the week are "in scope"
      days.push(buildDay(dateStr, y, monthNum, day, true, todayStr));
    }

    return days;
  }, [
    currentMonth,
    holidaysByDate,
    requestsByDate,
    eventsByDate,
    birthdaysByMonthDay,
    disabledDates,
    userOffice,
    user,
    selectedUserIds,
    showEvents,
    showBirthdays,
    disablePublicHolidays,
  ]);

  const monthRows = useMemo(() => {
    const rows: CalendarDay[][] = [];
    for (let i = 0; i < calendarData.length; i += 7) {
      rows.push(calendarData.slice(i, i + 7));
    }
    return rows;
  }, [calendarData]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const navigate = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const next = new Date(prev);
      if (view === 'week') {
        next.setDate(prev.getDate() + (direction === 'prev' ? -7 : 7));
      } else if (direction === 'prev') {
        next.setMonth(prev.getMonth() - 1);
      } else {
        next.setMonth(prev.getMonth() + 1);
      }
      return next;
    });
  };

  const handleViewChange = (
    _: React.MouseEvent<HTMLElement>,
    newView: CalendarView | null
  ) => {
    if (!newView || newView === view) return;

    if (newView === 'week') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      const todayWeekMonday = getMonday(today);
      const todayWeekSunday = new Date(todayWeekMonday);
      todayWeekSunday.setDate(todayWeekMonday.getDate() + 6);

      const intersects =
        todayWeekMonday <= monthEnd && todayWeekSunday >= monthStart;

      if (intersects) {
        setCurrentMonth(today);
      } else {
        // First Monday that falls inside the displayed month
        const firstMondayInMonth = new Date(monthStart);
        const dow = firstMondayInMonth.getDay();
        const daysUntilMonday = dow === 0 ? 1 : dow === 1 ? 0 : 8 - dow;
        firstMondayInMonth.setDate(firstMondayInMonth.getDate() + daysUntilMonday);
        setCurrentMonth(firstMondayInMonth);
      }
    } else {
      // Week → Month: show month of the week's Monday
      setCurrentMonth(getMonday(currentMonth));
    }

    setView(newView);
    setExpandedDate(null);
  };

  const headerTitle = useMemo(() => {
    if (view === 'month') {
      return `${monthNames[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
    }
    const monday = getMonday(currentMonth);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const weekNum = getWeekNumber(monday);
    const sameMonth = monday.getMonth() === sunday.getMonth();
    const range = sameMonth
      ? `${monday.getDate()}–${sunday.getDate()} ${shortMonth(monday)} ${monday.getFullYear()}`
      : `${monday.getDate()} ${shortMonth(monday)} – ${sunday.getDate()} ${shortMonth(sunday)} ${sunday.getFullYear()}`;
    return `W${weekNum} · ${range}`;
  }, [view, currentMonth]);

  const getTimeOffTypeColor = (type: TimeOffType): string => {
    switch (type) {
      case 'full_day':
        return '#a2ce64';
      case 'half_day':
        return '#bb4ee6ff';
      case 'remote':
        return '#d66910ff';
      default:
        return theme.palette.grey[500];
    }
  };

  const getTimeOffTypeLabel = (type: TimeOffType, halfDayPeriod?: string): string => {
    switch (type) {
      case 'full_day':
        return 'Full Day';
      case 'half_day':
        return halfDayPeriod === 'morning' ? 'AM' : 'PM';
      case 'remote':
        return 'Remote';
      default:
        return type;
    }
  };

  const renderDay = (day: CalendarDay, options?: { spacious?: boolean }) => {
    const spacious = options?.spacious ?? false;
    const isSelected = selectedDates.includes(day.date);
    const hasRequests = day.timeOffRequests.length > 0;
    const hasEvents = day.events && day.events.length > 0;
    const isExpanded = !spacious && expandedDate === day.date;
    const showFullLabels = spacious || isExpanded;

    const dayHolidays = publicHolidays.filter((h) => h.date === day.date);
    const hasBlockedHoliday = dayHolidays.some((holiday) => {
      if (!userOffice) return true;
      const holidayOffice = (holiday as { office?: string }).office;
      if (holidayOffice) return holidayOffice === userOffice || holidayOffice === 'ALL';
      const isUK = holiday.name.includes('(UK)');
      const isFR = holiday.name.includes('(FR)');
      if (isUK || isFR) return (userOffice === 'UK' && isUK) || (userOffice === 'FR' && isFR);
      return true;
    });

    const hasRequestableHoliday = dayHolidays.some((holiday) => {
      if (!userOffice) return false;
      const holidayOffice = (holiday as { office?: string }).office;
      if (holidayOffice) return holidayOffice !== userOffice && holidayOffice !== 'ALL';
      if (userOffice === 'UK' && holiday.name.includes('(FR)')) return true;
      if (userOffice === 'FR' && holiday.name.includes('(UK)')) return true;
      return false;
    });

    const isBlockedHoliday = hasBlockedHoliday;
    const isRequestableHoliday = hasRequestableHoliday && !hasBlockedHoliday;

    const selectAccent = isDark ? NAUTILUS.sand : NAUTILUS.deepOcean;
    const baseBgColor = isSelected
      ? alpha(selectAccent, 0.12)
      : isBlockedHoliday
        ? alpha(theme.palette.secondary.main, 0.08)
        : isRequestableHoliday
          ? alpha(theme.palette.warning.main, 0.08)
          : day.isToday
            ? alpha(theme.palette.info.main, 0.08)
            : 'transparent';

    const cellHeight = spacious
      ? { xs: 'auto', md: 280 }
      : { xs: 72, md: 60, lg: 80 };

    return (
      <Box
        key={day.date}
        sx={{
          position: 'relative',
          height: cellHeight,
          minHeight: spacious ? { xs: 120, md: 280 } : undefined,
          zIndex: isExpanded ? 100 : 1,
          opacity: day.isWeekend && spacious ? 0.85 : 1,
        }}
        onMouseEnter={() => !day.isDisabled && handleMouseEnter(day.date)}
        onMouseLeave={handleMouseLeave}
      >
        <motion.div
          layout={!spacious}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{
            position: isExpanded ? 'absolute' : 'relative',
            top: isExpanded ? -10 : 0,
            left: isExpanded ? -10 : 0,
            width: isExpanded ? '150%' : '100%',
            minHeight: isExpanded ? 120 : '100%',
            height: isExpanded ? 'auto' : '100%',
            backgroundColor: isExpanded
              ? (isDark ? NAUTILUS.deepOcean : '#ffffff')
              : baseBgColor,
            border: isExpanded || isSelected ? '2px solid' : '1px solid',
            borderColor: isExpanded || isSelected ? selectAccent : panelBorder,
            borderRadius: 4,
            padding: spacious ? 8 : 4,
            zIndex: isExpanded ? 10 : 1,
            boxShadow: 'none',
            cursor: day.isDisabled ? 'not-allowed' : 'pointer',
            overflowY: spacious ? 'auto' : undefined,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!day.isDisabled) onDateSelect(day.date);
          }}
        >
          {/* Date number + weekday in week view */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: day.isPublicHoliday
                  ? theme.palette.secondary.main
                  : day.isToday
                    ? theme.palette.info.main
                    : 'inherit',
                fontSize: spacious
                  ? { xs: '1rem', md: '1.1rem' }
                  : { md: '0.75rem', lg: '0.875rem' },
              }}
            >
              {new Date(day.date + 'T12:00:00').getDate()}
            </Typography>
            {spacious && isMobile && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {dayNames[(((new Date(day.date + 'T12:00:00').getDay()) + 6) % 7)]}
              </Typography>
            )}
          </Box>

          {/* Public holiday */}
          {day.isPublicHoliday && day.publicHolidayName && (
            <Tooltip
              title={
                !showFullLabels
                  ? dayHolidays.length === 1
                    ? day.publicHolidayName
                    : dayHolidays.map((h) => h.name).join('\n')
                  : ''
              }
            >
              <Chip
                label={day.publicHolidayName}
                size="small"
                sx={{
                  height: spacious ? 20 : 16,
                  fontSize: spacious
                    ? '0.7rem'
                    : { md: '0.55rem', lg: '0.6rem' },
                  backgroundColor: isBlockedHoliday
                    ? theme.palette.secondary.main
                    : isRequestableHoliday
                      ? theme.palette.warning.main
                      : theme.palette.secondary.main,
                  color: 'white',
                  mb: 0.5,
                  maxWidth: showFullLabels ? '100%' : '60px',
                  width: showFullLabels ? 'fit-content' : undefined,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </Tooltip>
          )}

          {/* Requests */}
          {hasRequests && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, width: '100%' }}>
              {(showFullLabels
                ? day.timeOffRequests
                : day.timeOffRequests.slice(0, 2)
              ).map((request) => {
                const isAdmin = hasCapability(user, CAP.APPROVE_TIME_OFF);
                const canApproveReject =
                  isAdmin &&
                  request.status === 'pending' &&
                  request.userId !== user?.id;

                return (
                  <Box
                    key={request.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.2,
                      mt: showFullLabels ? 0.25 : 0,
                    }}
                  >
                    <Tooltip
                      title={
                        !showFullLabels
                          ? `${request.userName}: ${getTimeOffTypeLabel(request.holidayType, request.halfDayPeriod)} (${request.status === 'pending' ? 'Pending' : 'Approved'})`
                          : ''
                      }
                    >
                      <Chip
                        label={`${request.userTrigram}${
                          showFullLabels
                            ? ` - ${getTimeOffTypeLabel(request.holidayType, request.halfDayPeriod)}`
                            : ''
                        }`}
                        size="small"
                        sx={{
                          height: spacious ? 22 : 16,
                          fontSize: spacious
                            ? '0.7rem'
                            : { md: '0.55rem', lg: '0.6rem' },
                          backgroundColor: getTimeOffTypeColor(request.holidayType),
                          color: 'white',
                          border:
                            request.status === 'pending'
                              ? `2px dotted ${theme.palette.mode === 'dark' ? '#fff' : '#333'}`
                              : 'none',
                          width: showFullLabels ? '100%' : 'auto',
                          justifyContent: 'flex-start',
                          '& .MuiChip-label': {
                            px: 0.5,
                            width: '100%',
                          },
                        }}
                      />
                    </Tooltip>
                    {showFullLabels && canApproveReject && (
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5, flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          aria-label={`Approve ${request.userName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onApproveRequest?.(request.id);
                          }}
                          sx={{
                            p: 0,
                            backgroundColor: 'success.light',
                            color: 'white',
                            '&:hover': { backgroundColor: 'success.main' },
                            width: 20,
                            height: 20,
                          }}
                        >
                          <CheckCircleIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Reject ${request.userName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRejectRequest?.(request.id);
                          }}
                          sx={{
                            p: 0,
                            backgroundColor: 'error.light',
                            color: 'white',
                            '&:hover': { backgroundColor: 'error.main' },
                            width: 20,
                            height: 20,
                          }}
                        >
                          <CancelIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    )}
                    {!showFullLabels && canApproveReject && (
                      <Box sx={{ display: 'flex', gap: 0.1 }}>
                        <IconButton
                          size="small"
                          aria-label={`Approve ${request.userName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onApproveRequest?.(request.id);
                          }}
                          sx={{
                            p: 0.2,
                            '& .MuiSvgIcon-root': { fontSize: '0.8rem' },
                            color: 'success.main',
                          }}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Reject ${request.userName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRejectRequest?.(request.id);
                          }}
                          sx={{
                            p: 0.2,
                            '& .MuiSvgIcon-root': { fontSize: '0.8rem' },
                            color: 'error.main',
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                );
              })}
              {!showFullLabels && day.timeOffRequests.length > 2 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { md: '0.55rem', lg: '0.6rem' },
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  +{day.timeOffRequests.length - 2}
                </Typography>
              )}
            </Box>
          )}

          {/* Events */}
          {hasEvents && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.3,
                width: '100%',
                mt: 0.5,
              }}
            >
              {(showFullLabels ? day.events! : day.events!.slice(0, 1)).map(
                (event) => (
                  <Tooltip
                    key={event.id}
                    title={
                      !showFullLabels
                        ? `${event.title}${event.description ? ': ' + event.description : ''}`
                        : ''
                    }
                  >
                    <Chip
                      label={event.title}
                      size="small"
                      sx={{
                        height: spacious ? 20 : 14,
                        fontSize: spacious
                          ? '0.65rem'
                          : { md: '0.5rem', lg: '0.55rem' },
                        backgroundColor: theme.palette.info.main,
                        color: 'white',
                        width: showFullLabels ? '100%' : 'auto',
                        justifyContent: 'flex-start',
                        '& .MuiChip-label': { px: 0.3 },
                      }}
                    />
                  </Tooltip>
                )
              )}
              {!showFullLabels && day.events!.length > 1 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { md: '0.45rem', lg: '0.5rem' },
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  +{day.events!.length - 1}
                </Typography>
              )}
            </Box>
          )}

          {/* Birthdays */}
          {day.birthdays && day.birthdays.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.3,
                width: '100%',
                mt: 0.5,
              }}
            >
              {(showFullLabels ? day.birthdays : day.birthdays.slice(0, 1)).map(
                (bUser) => (
                  <Tooltip
                    key={bUser.id}
                    title={!showFullLabels ? `${bUser.name}'s Birthday` : ''}
                  >
                    <Chip
                      icon={
                        <CakeIcon
                          sx={{
                            fontSize: '0.7rem !important',
                            color: 'inherit !important',
                          }}
                        />
                      }
                      label={
                        showFullLabels
                          ? `${bUser.name}'s Birthday`
                          : bUser.trigram || bUser.name.split(' ')[0]
                      }
                      size="small"
                      sx={{
                        height: spacious ? 20 : 14,
                        fontSize: spacious
                          ? '0.65rem'
                          : { md: '0.5rem', lg: '0.55rem' },
                        backgroundColor: '#ff69b4',
                        color: 'white',
                        width: showFullLabels ? '100%' : 'auto',
                        justifyContent: 'flex-start',
                        '& .MuiChip-label': { px: 0.3 },
                        '& .MuiChip-icon': { ml: 0.5, mr: -0.2 },
                      }}
                    />
                  </Tooltip>
                )
              )}
              {!showFullLabels && day.birthdays.length > 1 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { md: '0.45rem', lg: '0.5rem' },
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  +{day.birthdays.length - 1} birthdays
                </Typography>
              )}
            </Box>
          )}
        </motion.div>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        borderRadius: 1,
        boxShadow: 'none',
        border: `1px solid ${panelBorder}`,
        bgcolor: isDark ? NAUTILUS.deepOcean : '#ffffff',
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        {/* Calendar Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
            px: 0.5,
            gap: 1,
          }}
        >
          <IconButton
            onClick={() => navigate('prev')}
            size="small"
            aria-label={view === 'week' ? 'Previous week' : 'Previous month'}
            sx={{
              color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
              '&:hover': {
                bgcolor: alpha(NAUTILUS.sand, isDark ? 0.15 : 0.1),
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
              textAlign: 'center',
              flex: 1,
              letterSpacing: view === 'week' ? '0.02em' : '-0.01em',
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            {headerTitle}
          </Typography>

          <IconButton
            onClick={() => navigate('next')}
            size="small"
            aria-label={view === 'week' ? 'Next week' : 'Next month'}
            sx={{
              color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
              '&:hover': {
                bgcolor: alpha(NAUTILUS.sand, isDark ? 0.15 : 0.1),
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewChange}
            size="small"
            aria-label="Calendar view"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                borderColor: panelBorder,
                color: isDark ? '#b0bec5' : '#546e7a',
                '&.Mui-selected': {
                  bgcolor: isDark
                    ? alpha(NAUTILUS.sand, 0.15)
                    : alpha(NAUTILUS.deepOcean, 0.08),
                  color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                  borderColor: panelBorder,
                  '&:hover': {
                    bgcolor: isDark
                      ? alpha(NAUTILUS.sand, 0.22)
                      : alpha(NAUTILUS.deepOcean, 0.12),
                  },
                },
              },
            }}
          >
            <ToggleButton value="month" aria-label="Month view">
              Month
            </ToggleButton>
            <ToggleButton value="week" aria-label="Week view">
              Week
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Manifest filter bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2.5,
            px: 0.5,
            flexWrap: 'wrap',
            pb: 2,
            borderBottom: `1px solid ${panelBorder}`,
          }}
        >
          <Autocomplete
            multiple
            size="small"
            options={personOptions}
            value={selectedPeople}
            onChange={(_, value) => setSelectedPeople(value)}
            getOptionLabel={(opt) =>
              opt.userTrigram
                ? `${opt.userTrigram} — ${opt.userName}`
                : opt.userName
            }
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            slotProps={getZoomCompensatedAutocompleteSlotProps()}
            renderInput={(params) => (
              <TextField
                {...params}
                label="People"
                placeholder={
                  selectedPeople.length === 0 ? 'All people' : undefined
                }
              />
            )}
            sx={{
              minWidth: { xs: '100%', sm: 240 },
              flex: { sm: '1 1 240px' },
              maxWidth: 420,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
            ChipProps={{
              size: 'small',
              sx: {
                height: 22,
                fontSize: '0.7rem',
              },
            }}
          />

          <Chip
            label="Events"
            size="small"
            clickable
            onClick={() => setShowEvents((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowEvents((v) => !v);
              }
            }}
            variant={showEvents ? 'filled' : 'outlined'}
            aria-pressed={showEvents}
            role="button"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              bgcolor: showEvents
                ? theme.palette.info.main
                : 'transparent',
              color: showEvents
                ? '#fff'
                : isDark
                  ? '#b0bec5'
                  : '#546e7a',
              borderColor: panelBorder,
              '&:hover': {
                bgcolor: showEvents
                  ? alpha(theme.palette.info.main, 0.85)
                  : alpha(theme.palette.info.main, 0.08),
              },
            }}
          />

          <Chip
            label="Birthdays"
            size="small"
            clickable
            icon={
              <CakeIcon
                sx={{
                  fontSize: '0.85rem !important',
                  color: showBirthdays ? '#fff !important' : 'inherit !important',
                }}
              />
            }
            onClick={() => setShowBirthdays((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowBirthdays((v) => !v);
              }
            }}
            variant={showBirthdays ? 'filled' : 'outlined'}
            aria-pressed={showBirthdays}
            role="button"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              bgcolor: showBirthdays ? '#ff69b4' : 'transparent',
              color: showBirthdays
                ? '#fff'
                : isDark
                  ? '#b0bec5'
                  : '#546e7a',
              borderColor: panelBorder,
              '&:hover': {
                bgcolor: showBirthdays
                  ? alpha('#ff69b4', 0.85)
                  : alpha('#ff69b4', 0.08),
              },
            }}
          />
        </Box>

        {/* Month view */}
        {view === 'month' && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '40px repeat(7, 1fr)',
                mb: 1,
                gap: 0,
              }}
            >
              <Box />
              {dayNames.map((d) => (
                <Typography
                  key={d}
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 500,
                    color: 'text.secondary',
                    fontSize: { md: '0.7rem', lg: '0.75rem' },
                  }}
                >
                  {d}
                </Typography>
              ))}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {monthRows.map((row) => {
                const weekNum = getWeekNumber(new Date(row[0].date + 'T12:00:00'));
                return (
                  <Box
                    key={row[0].date}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '40px repeat(7, 1fr)',
                      alignItems: 'stretch',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pr: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          letterSpacing: '0.06em',
                          color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                          opacity: 0.7,
                          fontFamily: 'monospace',
                        }}
                      >
                        W{weekNum}
                      </Typography>
                    </Box>
                    {row.map((day) => (
                      <Box key={day.date} sx={{ minWidth: 0 }}>
                        {renderDay(day)}
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {/* Week view */}
        {view === 'week' && (
          <>
            {!isMobile && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  mb: 1,
                }}
              >
                {dayNames.map((d) => (
                  <Typography
                    key={d}
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 500,
                      color: 'text.secondary',
                      fontSize: { md: '0.7rem', lg: '0.75rem' },
                    }}
                  >
                    {d}
                  </Typography>
                ))}
              </Box>
            )}

            <Box
              sx={{
                display: isMobile ? 'flex' : 'grid',
                flexDirection: isMobile ? 'column' : undefined,
                gridTemplateColumns: isMobile ? undefined : 'repeat(7, 1fr)',
                gap: isMobile ? 1.5 : 0.5,
              }}
            >
              {weekData.map((day) => (
                <Box key={day.date} sx={{ minWidth: 0 }}>
                  {renderDay(day, { spacious: true })}
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Legend */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 1,
            bgcolor: isDark
              ? alpha(NAUTILUS.abyssal, 0.5)
              : alpha('#f5f5f5', 0.5),
            border: `1px solid ${panelBorder}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
              mr: 1,
              fontSize: '0.7rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Legend:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden
              sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#a2ce64' }}
            />
            <Typography variant="caption">Full Day</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden
              sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#bb4ee6' }}
            />
            <Typography variant="caption">Half Day</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden
              sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#d66910' }}
            />
            <Typography variant="caption">Remote</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden
              sx={{
                width: 12,
                height: 12,
                backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                border: `1px solid ${theme.palette.secondary.main}`,
              }}
            />
            <Typography variant="caption">Your Office Holiday</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden
              sx={{
                width: 12,
                height: 12,
                backgroundColor: alpha(theme.palette.warning.main, 0.2),
                border: `1px solid ${theme.palette.warning.main}`,
              }}
            />
            <Typography variant="caption">Other Office Holiday</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: theme.palette.info.main,
              }}
            />
            <Typography variant="caption">Company Event</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CakeIcon aria-hidden sx={{ fontSize: '0.85rem', color: '#ff69b4' }} />
            <Typography variant="caption">Birthday</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: isDark ? NAUTILUS.deepOcean : '#fff',
                border: `2px dotted ${isDark ? '#fff' : '#333'}`,
              }}
            />
            <Typography variant="caption">Pending Request</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TimeOffCalendar;
